import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Plus, Search, Building2, Download, ClipboardPaste, FileSpreadsheet } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import StateBadge from "@/components/StateBadge";
import Modal from "@/components/Modal";
import EmptyState from "@/components/EmptyState";
import BulkImportModal from "@/components/BulkImportModal";
import BulkUpdateModal from "@/components/BulkUpdateModal";
import PropertyExcelImportModal from "@/components/PropertyExcelImportModal";
import { PROPERTY_STATE_LABELS } from "@/lib/dictionaries";
import { useAuth } from "@/contexts/AuthContext";

const EMPTY_FORM = {
    code: "",
    name: "",
    address: "",
    type: "apartment",
    bedrooms: 1,
    bathrooms: 1,
    capacity: 2,
    state: "free",
    client_id: "",
    notes: "",
};

export default function Properties() {
    const { meta, user } = useAuth();
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filter, setFilter] = useState("all");
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const [bulkOpen, setBulkOpen] = useState(false);
    const [bulkUpdateOpen, setBulkUpdateOpen] = useState(false);
    const [excelImportOpen, setExcelImportOpen] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [clients, setClients] = useState([]);
    const [error, setError] = useState("");
    const [saving, setSaving] = useState(false);

    const load = () => {
        setLoading(true);
        api.get("/properties")
            .then((r) => setItems(r.data || []))
            .finally(() => setLoading(false));
    };

    useEffect(() => {
        load();
        api.get("/clients").then((r) => setClients(r.data || [])).catch(() => {});
    }, []);

    const filtered = useMemo(() => {
        return items.filter((p) => {
            if (filter !== "all" && p.state !== filter) return false;
            if (search) {
                const s = search.toLowerCase();
                if (
                    !p.name.toLowerCase().includes(s) &&
                    !p.code.toLowerCase().includes(s) &&
                    !p.address.toLowerCase().includes(s)
                )
                    return false;
            }
            return true;
        });
    }, [items, filter, search]);

    const submit = async (e) => {
        e?.preventDefault?.();
        setSaving(true);
        setError("");
        try {
            await api.post("/properties", {
                ...form,
                bedrooms: Number(form.bedrooms),
                bathrooms: Number(form.bathrooms),
                capacity: Number(form.capacity),
                client_id: form.client_id || null,
            });
            setOpen(false);
            setForm(EMPTY_FORM);
            load();
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    const states = meta?.property_states || Object.keys(PROPERTY_STATE_LABELS);

    return (
        <div className="space-y-6" data-testid="properties-page">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <p className="text-[11px] font-semibold tracking-widest uppercase text-zentra-accent-strong">
                        Cartera operativa
                    </p>
                    <h2 className="font-display text-3xl font-bold text-zentra-ink tracking-tight mt-1">
                        Propiedades
                    </h2>
                    <p className="text-sm text-zentra-muted mt-1">
                        {items.length} propiedades · {filtered.length} mostradas
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    {["admin", "super_admin"].includes(user?.role) && (
                        <button type="button" className="zentra-btn-ghost" onClick={() => setExcelImportOpen(true)} data-testid="property-excel-import-button">
                            <FileSpreadsheet size={16} /> Importar Excel
                        </button>
                    )}
                    <button
                        type="button"
                        className="zentra-btn-ghost"
                        onClick={() => setBulkUpdateOpen(true)}
                        data-testid="bulk-update-button"
                    >
                        <ClipboardPaste size={16} />
                        Actualizar en masa
                    </button>
                    <button
                        type="button"
                        className="zentra-btn-ghost"
                        onClick={() => setBulkOpen(true)}
                        data-testid="bulk-import-button"
                    >
                        <Download size={16} />
                        Importar Avaibook / iCal
                    </button>
                    <button
                        type="button"
                        className="zentra-btn-primary"
                        onClick={() => setOpen(true)}
                        data-testid="new-property-button"
                    >
                        <Plus size={16} />
                        Nueva propiedad
                    </button>
                </div>
            </div>

            {/* Filters */}
            <div className="zentra-card-static p-3 flex items-center gap-3 flex-wrap" data-testid="property-filters">
                <div className="flex items-center gap-2 px-3 py-2 bg-zentra-bg border border-zentra-border rounded-md text-sm flex-1 min-w-[200px]">
                    <Search size={14} className="text-zentra-muted" />
                    <input
                        className="bg-transparent outline-none border-0 flex-1 text-sm"
                        placeholder="Buscar por nombre, código, dirección…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        data-testid="property-search"
                    />
                </div>
                <div className="flex items-center gap-1.5 overflow-x-auto">
                    <FilterChip active={filter === "all"} onClick={() => setFilter("all")} label="Todas" testId="filter-all" />
                    {states.map((s) => (
                        <FilterChip
                            key={s}
                            active={filter === s}
                            onClick={() => setFilter(s)}
                            label={PROPERTY_STATE_LABELS[s]?.label || s}
                            testId={`filter-${s}`}
                        />
                    ))}
                </div>
            </div>

            {/* Grid */}
            {loading ? (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {Array.from({ length: 6 }).map((_, i) => (
                        <div key={i} className="zentra-skeleton h-44" />
                    ))}
                </div>
            ) : filtered.length === 0 ? (
                <EmptyState
                    icon={Building2}
                    title="Sin propiedades"
                    description="Crea la primera propiedad para empezar a operar."
                    action={
                        <button onClick={() => setOpen(true)} className="zentra-btn-primary mt-2">
                            <Plus size={14} /> Nueva propiedad
                        </button>
                    }
                />
            ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="property-grid">
                    {filtered.map((p) => {
                        const owner = p.client_id ? clients.find((c) => c.id === p.client_id) : null;
                        return (
                        <Link
                            key={p.id}
                            to={`/propiedades/${p.id}`}
                            className="zentra-card p-5 flex flex-col gap-3 group"
                            data-testid={`property-card-${p.code}`}
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div className="min-w-0">
                                    <p className="text-[11px] font-mono-zentra tracking-wider text-zentra-muted uppercase">
                                        {p.code}
                                    </p>
                                    <h3 className="font-display font-semibold text-zentra-ink leading-tight mt-0.5 line-clamp-1">
                                        {p.name}
                                    </h3>
                                </div>
                                <StateBadge value={p.state} dict={PROPERTY_STATE_LABELS} testId={`state-${p.code}`} />
                            </div>
                            <p className="text-sm text-zentra-muted line-clamp-1">{p.address}</p>
                            {owner ? (
                                <span
                                    className="self-start inline-flex items-center gap-1.5 text-[11px] font-semibold tracking-wide px-2 py-0.5 rounded-full bg-zentra-accent/10 text-zentra-accent-strong border border-zentra-accent/25"
                                    data-testid={`property-owner-${p.code}`}
                                >
                                    {owner.name}
                                </span>
                            ) : (
                                <span className="self-start inline-flex text-[10px] font-bold tracking-widest uppercase px-2 py-0.5 rounded-full bg-zentra-bg text-zentra-muted border border-zentra-border">
                                    Sin cliente
                                </span>
                            )}
                            <div className="flex items-center gap-3 mt-auto pt-3 border-t border-zentra-border text-xs text-zentra-muted">
                                <span>{p.bedrooms} hab</span>
                                <span>•</span>
                                <span>{p.bathrooms} baños</span>
                                <span>•</span>
                                <span>{p.capacity} pax</span>
                            </div>
                        </Link>
                        );
                    })}
                </div>
            )}

            {/* New property modal */}
            <Modal
                open={open}
                onClose={() => setOpen(false)}
                title="Nueva propiedad"
                subtitle="Añade un apartamento o inmueble a tu cartera operativa."
                wide
                testId="property-modal"
                footer={
                    <>
                        <button type="button" className="zentra-btn-ghost" onClick={() => setOpen(false)}>
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            form="property-form"
                            className="zentra-btn-primary"
                            disabled={saving}
                            data-testid="save-property"
                        >
                            {saving ? "Guardando…" : "Crear propiedad"}
                        </button>
                    </>
                }
            >
                <form id="property-form" onSubmit={submit} className="grid grid-cols-2 gap-4">
                    <div>
                        <label className="zentra-label">Código</label>
                        <input
                            className="zentra-input"
                            required
                            value={form.code}
                            onChange={(e) => setForm({ ...form, code: e.target.value })}
                            placeholder="VAL-001"
                            data-testid="property-code"
                        />
                    </div>
                    <div>
                        <label className="zentra-label">Estado</label>
                        <select
                            className="zentra-select"
                            value={form.state}
                            onChange={(e) => setForm({ ...form, state: e.target.value })}
                            data-testid="property-state"
                        >
                            {states.map((s) => (
                                <option key={s} value={s}>
                                    {PROPERTY_STATE_LABELS[s]?.label || s}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Nombre interno</label>
                        <input
                            className="zentra-input"
                            required
                            value={form.name}
                            onChange={(e) => setForm({ ...form, name: e.target.value })}
                            placeholder="Apartamento Mar Azul"
                            data-testid="property-name"
                        />
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Dirección</label>
                        <input
                            className="zentra-input"
                            required
                            value={form.address}
                            onChange={(e) => setForm({ ...form, address: e.target.value })}
                            placeholder="C/ Marina 12, 4ºA, Valencia"
                            data-testid="property-address"
                        />
                    </div>
                    <div>
                        <label className="zentra-label">Habitaciones</label>
                        <input
                            type="number"
                            min="0"
                            className="zentra-input"
                            value={form.bedrooms}
                            onChange={(e) => setForm({ ...form, bedrooms: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="zentra-label">Baños</label>
                        <input
                            type="number"
                            min="0"
                            className="zentra-input"
                            value={form.bathrooms}
                            onChange={(e) => setForm({ ...form, bathrooms: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="zentra-label">Capacidad</label>
                        <input
                            type="number"
                            min="1"
                            className="zentra-input"
                            value={form.capacity}
                            onChange={(e) => setForm({ ...form, capacity: e.target.value })}
                        />
                    </div>
                    <div>
                        <label className="zentra-label">Cliente</label>
                        <select
                            className="zentra-select"
                            value={form.client_id}
                            onChange={(e) => setForm({ ...form, client_id: e.target.value })}
                        >
                            <option value="">Sin asignar</option>
                            {clients.map((c) => (
                                <option key={c.id} value={c.id}>
                                    {c.name}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="col-span-2">
                        <label className="zentra-label">Observaciones</label>
                        <textarea
                            className="zentra-textarea min-h-[80px]"
                            value={form.notes}
                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                            placeholder="Accesos, prioridades, observaciones…"
                        />
                    </div>
                    {error && (
                        <div className="col-span-2 rounded-md border border-red-200 bg-red-50 text-red-800 px-3 py-2 text-sm">
                            {error}
                        </div>
                    )}
                </form>
            </Modal>

            <BulkImportModal
                open={bulkOpen}
                onClose={() => setBulkOpen(false)}
                defaultSource="avaibook"
                onImported={load}
            />

            <BulkUpdateModal
                open={bulkUpdateOpen}
                onClose={() => setBulkUpdateOpen(false)}
                onUpdated={load}
            />
            <PropertyExcelImportModal
                open={excelImportOpen}
                onClose={() => setExcelImportOpen(false)}
                onDone={load}
            />
        </div>
    );
}

function FilterChip({ active, onClick, label, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={`px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap transition border ${
                active
                    ? "bg-zentra-primary text-white border-zentra-primary"
                    : "bg-white text-zentra-ink border-zentra-border hover:border-zentra-olive"
            }`}
            data-testid={testId}
        >
            {label}
        </button>
    );
}
